import axios, { AxiosInstance } from "axios";
import { stringify } from "query-string";
import fileDownload from "js-file-download";
import {
  IDataContextProvider,
  IHttpError,
  ICrudOperators,
  ICrudSorting,
  ICrudFilters,
} from "@modir/core";

const axiosInstance = axios.create();

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const customError: IHttpError = {
      ...error,
      message: error.response?.data?.message,
      statusCode: error.response?.status,
    };

    return Promise.reject(customError);
  }
);

const mapOperator = (operator: ICrudOperators): string => {
  switch (operator) {
    case "ne":
    case "gte":
    case "lte":
      return `_${operator}`;
    case "contains":
      return "_like";
    case "eq":
    default:
      return "";
  }
};

const generateSort = (sort?: ICrudSorting) => {
  if (sort && sort.length > 0) {
    const _sort: string[] = [];
    const _order: string[] = [];

    sort.map((item) => {
      _sort.push(item.field);
      _order.push(item.order);
    });

    return {
      _sort,
      _order,
    };
  }

  return;
};

const generateFilter = (filters?: ICrudFilters) => {
  const queryFilters: { [key: string]: string } = {};
  if (filters) {
    filters.map((filter) => {
      if (filter.operator !== "or") {
        const { field, operator, value } = filter;

        if (field === "q") {
          queryFilters[field] = value;
          return;
        }

        const mappedOperator = mapOperator(operator);
        queryFilters[`${field}${mappedOperator}`] = value;
      }
    });
  }

  return queryFilters;
};

function formData_append_object(fd: any, obj: any, key?: string) {
  var i, k;
  for (i in obj) {
    k = key ? key + "[" + i + "]" : i;
    if (typeof obj[i] == "object") formData_append_object(fd, obj[i], k);
    else fd.append(k, obj[i] ?? "");
  }
}

const JsonServer = (
  apiUrl: string,
  httpClient: AxiosInstance = axiosInstance
): IDataContextProvider => ({
  getList: async ({
    resource,
    hasPagination = true,
    pagination = { current: 1, pageSize: 10 },
    filters,
    sort,
    headers,
  }) => {
    const url = `${apiUrl}/${resource}`;
    const { current = 1, pageSize = 10 } = pagination ?? {};

    const queryFilters = generateFilter(filters);

    const query: {
      _start?: number;
      _end?: number;
      _sort?: string;
      _order?: string;
    } = hasPagination
      ? {
          _start: (current - 1) * pageSize,
          _end: current * pageSize,
        }
      : {};

    const generatedSort = generateSort(sort);
    if (generatedSort) {
      const { _sort, _order } = generatedSort;
      query._sort = _sort.join(",");
      query._order = _order.join(",");
    }

    if (headers) {
      httpClient.defaults.headers = {
        ...httpClient.defaults.headers,
        ...headers,
      };
    }

    const { data, headers: httpClientHeaders } = await httpClient.get(
      `${url}?${stringify(query)}&${stringify(queryFilters)}`
    );

    const total = +httpClientHeaders["x-total-count"];

    return {
      data,
      total,
    };
  },

  getMany: async ({ resource, ids, headers }) => {
    if (headers) {
      httpClient.defaults.headers = {
        ...httpClient.defaults.headers,
        ...headers,
      };
    }

    const { data } = await httpClient.get(
      `${apiUrl}/${resource}?${stringify({ id: ids })}`
    );

    return {
      data,
    };
  },

  getOne: async ({ resource, id, headers }) => {
    const url = `${apiUrl}/${resource}/${id}`;

    if (headers) {
      httpClient.defaults.headers = {
        ...httpClient.defaults.headers,
        ...headers,
      };
    }

    const { data } = await httpClient.get(url);

    return {
      data,
    };
  },

  create: async ({ resource, variables, headers }) => {
    const url = `${apiUrl}/${resource}`;

    if (headers) {
      httpClient.defaults.headers = {
        ...httpClient.defaults.headers,
        ...headers,
      };
    }

    const { data } = await httpClient.post(url, variables);

    return {
      data,
    };
  },

  createMany: async ({ resource, variables, headers }) => {
    if (headers) {
      httpClient.defaults.headers = {
        ...httpClient.defaults.headers,
        ...headers,
      };
    }

    const response = await Promise.all(
      variables.map(async (param) => {
        const { data } = await httpClient.post(`${apiUrl}/${resource}`, param);
        return data;
      })
    );

    return { data: response };
  },

  update: async ({ resource, id, variables, headers }) => {
    const url = `${apiUrl}/${resource}/${id}`;

    if (headers) {
      httpClient.defaults.headers = {
        ...httpClient.defaults.headers,
        ...headers,
      };
    }

    const { data } = await httpClient.patch(url, variables);

    return {
      data,
    };
  },

  updateMany: async ({ resource, ids, variables, headers }) => {
    if (headers) {
      httpClient.defaults.headers = {
        ...httpClient.defaults.headers,
        ...headers,
      };
    }

    const response = await Promise.all(
      ids.map(async (id) => {
        const { data } = await httpClient.patch(
          `${apiUrl}/${resource}/${id}`,
          variables
        );
        return data;
      })
    );

    return { data: response };
  },

  deleteOne: async ({ resource, id, variables, headers }) => {
    const url = `${apiUrl}/${resource}/${id}`;

    if (headers) {
      httpClient.defaults.headers = {
        ...httpClient.defaults.headers,
        ...headers,
      };
    }

    const { data } = await httpClient.delete(url, variables as any);

    return {
      data,
    };
  },

  postFormData: async ({ resource, variables, headers }: any) => {
    const url = `${apiUrl}/${resource}`;

    if (headers) {
      httpClient.defaults.headers = {
        ...httpClient.defaults.headers,
        ...headers,
      };
    }

    let form_data = new FormData();
    if (variables) {
      formData_append_object(form_data, variables);
      // for (const field of Object.keys(variables))
      //   if (typeof variables[field] === "object") {
      //     form_data.append(field, JSON.stringify(variables[field]));
      //   } else {
      //     form_data.append(field, variables[field]);
      //   }
    }

    const { data } = await httpClient.post(url, form_data);

    return {
      data,
    };
  },

  upload: async ({ resource, variables, headers }: any) => {
    const url = `${apiUrl}/${resource}`;

    if (headers) {
      httpClient.defaults.headers = {
        ...httpClient.defaults.headers,
        ...headers,
        ...{
          "Content-Type": "multipart/form-data",
          Accept: "*/*",
        },
      };
    }

    let form_data = new FormData();
    if (variables) {
      for (const field of Object.keys(variables))
        form_data.append(field, variables[field]);
    }

    const { data } = await httpClient.post(url, form_data);

    return {
      data,
    };
  },
  //TODO:not tested yet
  download: async ({ resource, variables, headers }: any) => {
    const url = `${apiUrl}/${resource}`;

    if (headers) {
      httpClient.defaults.headers = {
        ...httpClient.defaults.headers,
        ...headers,
        ...{
          responseType: "blob",
        },
      };
    }

    return httpClient.post(url, variables).then((response) => {
      if (response.status === 200) {
        let filename = "";
        let disposition = response.headers["content-disposition"];
        if (disposition) {
          var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          var matches = filenameRegex.exec(disposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, "");
          }
        }
        return fileDownload(
          response.data,
          filename,
          response.headers["content-type"]
        );
      }
    });
  },

  deleteMany: async ({ resource, ids, variables, headers }) => {
    if (headers) {
      httpClient.defaults.headers = {
        ...httpClient.defaults.headers,
        ...headers,
      };
    }

    const response = await Promise.all(
      ids.map(async (id) => {
        const { data } = await httpClient.delete(
          `${apiUrl}/${resource}/${id}`,
          variables as any
        );
        return data;
      })
    );
    return { data: response };
  },

  custom: async ({ url, method, filters, sort, payload, query, headers }) => {
    let requestUrl = `${url}?`;

    if (sort) {
      const generatedSort = generateSort(sort);
      if (generatedSort) {
        const { _sort, _order } = generatedSort;
        const sortQuery = {
          _sort: _sort.join(","),
          _order: _order.join(","),
        };
        requestUrl = `${requestUrl}&${stringify(sortQuery)}`;
      }
    }

    if (filters) {
      const filterQuery = generateFilter(filters);
      requestUrl = `${requestUrl}&${stringify(filterQuery)}`;
    }

    if (query) {
      requestUrl = `${requestUrl}&${stringify(query)}`;
    }

    if (headers) {
      httpClient.defaults.headers = {
        ...httpClient.defaults.headers,
        ...headers,
      };
    }

    let axiosResponse;
    switch (method) {
      case "put":
      case "post":
      case "patch":
        axiosResponse = await httpClient[method](url, payload);
        break;
      case "delete":
        axiosResponse = await httpClient.delete(url);
        break;
      default:
        axiosResponse = await httpClient.get(requestUrl);
        break;
    }

    const { data } = axiosResponse;

    return Promise.resolve({ data });
  },

  getApiUrl: () => {
    return apiUrl;
  },
});

export default JsonServer;
